import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Calendar,
  CheckCircle2,
  Goal,
  Landmark,
  ShieldAlert,
  Sparkles,
  Stethoscope,
  Target,
  TrendingUp,
  XCircle,
  Zap,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  VideoCard,
} from "../shared/categoryDetailShared";

const easeOut = [0.22, 1, 0.36, 1];

const iconFor = (key) => {
  const map = {
    chart: TrendingUp,
    goal: Goal,
    stadium: Landmark,
    injury: Stethoscope,
    calendar: Calendar,
  };
  return map[key] ?? BarChart3;
};

/** Betting-native section header — not the legal slate stack. */
function BettingSectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="relative mb-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-gradient-to-r from-orange-500 to-[#FFD221]" aria-hidden />
        <span className="text-[11px] font-black uppercase tracking-[0.28em] text-orange-600">{eyebrow}</span>
      </div>
      <h2 className="mt-3 text-2xl font-black leading-[1.15] tracking-tight text-stone-950 sm:text-3xl lg:text-[2.05rem]">
        {title}
      </h2>
      {subtitle ? <p className="mt-2 max-w-2xl text-sm font-semibold text-stone-600">{subtitle}</p> : null}
    </div>
  );
}

function CheckList({ items, color, light = false }) {
  const text = light ? "text-orange-50/95" : "text-stone-800";
  return (
    <ul className="space-y-2.5">
      {items.map((line) => (
        <li key={line} className={`flex gap-3 text-sm font-semibold leading-relaxed sm:text-[0.9375rem] ${text}`}>
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color }} strokeWidth={2.2} />
          <span>{line}</span>
        </li>
      ))}
    </ul>
  );
}

function WarningPanel({ title, children, tone = "amber" }) {
  const tones = {
    amber: "border-orange-300/60 bg-gradient-to-br from-orange-50/90 via-white to-amber-50/50",
    red: "border-red-300/70 bg-gradient-to-br from-red-50/90 to-white",
  };
  return (
    <div className={`rounded-2xl border-2 p-5 shadow-sm sm:p-6 ${tones[tone] ?? tones.amber}`}>
      <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-stone-900">
        <AlertTriangle className="h-4 w-4 shrink-0 text-orange-600" />
        {title}
      </p>
      <div className="mt-3 text-sm font-semibold leading-relaxed text-stone-800">{children}</div>
    </div>
  );
}

/** Top overview block — minimal light base, charcoal image panel, clean split. */
function BettingOverviewKickoff({ data }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: easeOut }}
      className="overflow-hidden rounded-[1.75rem] border border-stone-200/80 bg-white shadow-[0_24px_80px_-40px_rgba(15,23,42,0.14)] sm:rounded-[2rem]"
    >
      <div className="grid lg:grid-cols-2 lg:items-stretch">

        {/* ── LEFT: text content ────────────────────── */}
        <div className="relative flex flex-col justify-center overflow-hidden px-7 py-10 sm:px-10 sm:py-12 lg:px-12 lg:py-14">
          {/* subtle top-left charcoal corner wash */}
          <div
            className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full opacity-[0.055] blur-3xl"
            style={{ background: "#1c1917" }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-12 -right-12 h-48 w-48 rounded-full opacity-[0.04] blur-2xl"
            style={{ background: "#ea580c" }}
            aria-hidden
          />

          {/* eyebrow chips */}
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-white">
              Betting &amp; Gaming · Ghana
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200/80 bg-orange-50/80 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-orange-700">
              <Target className="h-3 w-3 shrink-0" />
              Education hub
            </span>
          </div>

          <h2 className="text-[1.65rem] font-black leading-[1.1] tracking-tight text-stone-950 sm:text-[2rem] lg:text-[2.1rem]">
            {data.introduction.headline}
          </h2>
          <p className="mt-4 max-w-prose text-[0.9375rem] leading-relaxed text-stone-500">
            {data.introduction.lead}
          </p>

          {/* two info pills stacked */}
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
              <p className="text-[9px] font-black uppercase tracking-[0.24em] text-orange-600">Everyday searches</p>
              <ul className="mt-2.5 space-y-1.5">
                {data.introduction.searches.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-[0.8125rem] font-semibold text-stone-800">
                    <Zap className="h-3 w-3 shrink-0 text-orange-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
              <p className="text-[9px] font-black uppercase tracking-[0.24em] text-orange-600">Why demand is high</p>
              <ul className="mt-2.5 space-y-1.5">
                {data.introduction.drivers.map((d) => (
                  <li key={d} className="flex items-center gap-2 text-[0.8125rem] font-semibold text-stone-800">
                    <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" strokeWidth={2.5} />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* research note */}
          <p className="mt-5 text-[0.8rem] leading-relaxed text-stone-400">
            {data.introduction.researchNote}
          </p>

          {/* focus area chips */}
          <div className="mt-5 flex flex-wrap gap-1.5">
            {data.focusAreas.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-stone-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-600 shadow-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* ── RIGHT: single hero image + signal board ──────────────── */}
        <div className="relative flex min-h-0 flex-col border-t border-stone-100 lg:min-h-full lg:border-l lg:border-t-0">
          <div className="absolute inset-0 bg-stone-950" />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
            aria-hidden
          />
          <div className="pointer-events-none absolute -right-12 -top-12 h-56 w-56 rounded-full bg-orange-500/20 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-48 w-48 rounded-full bg-[#FFD221]/12 blur-3xl" aria-hidden />

          <div className="relative flex flex-1 flex-col justify-center px-5 pb-2 pt-8 sm:px-8 sm:pb-3 sm:pt-10 lg:min-h-0 lg:px-8 lg:pt-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-24px" }}
              transition={{ duration: 0.5, ease: easeOut }}
              className="flex min-h-[min(52vh,22rem)] w-full flex-1 flex-col sm:min-h-[min(48vh,26rem)] lg:min-h-0"
            >
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-0 flex-1 flex-col rounded-[1.2rem] border-[5px] border-white bg-white p-1 shadow-[0_28px_70px_-25px_rgba(0,0,0,0.55)] ring-1 ring-white/15 sm:rounded-[1.35rem] sm:border-[6px] sm:p-1.5">
                  <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-stone-200">
                    <img
                      src={data.galleryImages?.[0] ?? data.heroImage}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* signal board strip */}
          <div className="relative shrink-0 border-t border-white/[0.07] px-5 py-5 sm:px-8 sm:py-6 lg:px-6">
            <p className="mb-3 text-[9px] font-black uppercase tracking-[0.28em] text-white/40">Signal board</p>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {data.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-white/[0.08] bg-white/[0.06] px-2 py-3 text-center backdrop-blur-sm sm:px-3"
                >
                  <p className={`text-base font-black tabular-nums sm:text-lg ${stat.value.includes("#") ? "text-orange-400" : "text-white"}`}>
                    {stat.value}
                  </p>
                  <p className="mt-0.5 text-[8px] font-bold uppercase leading-tight tracking-wide text-white/40 sm:text-[9px]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-[10px] font-medium text-white/28">
              Predictions as analysis — never guarantees.
            </p>
          </div>
        </div>

      </div>
    </motion.section>
  );
}

function SurfaceCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-orange-200/40 bg-white p-6 shadow-[0_20px_55px_-40px_rgba(234,88,12,0.22)] ring-1 ring-orange-950/[0.04] sm:p-8 ${className}`.trim()}
    >
      {children}
    </div>
  );
}

export const SportsBettingTipsOverviewTab = ({ data, color }) => (
  <div className="relative space-y-14">
    <div className="pointer-events-none absolute inset-x-0 -top-4 h-40 bg-gradient-to-b from-orange-100/25 to-transparent blur-2xl" />
    <BettingOverviewKickoff data={data} />

    <section className="relative">
      <BettingSectionHead eyebrow="Basics" title={data.plainLanguage.headline} />
      <SurfaceCard>
        <p className="max-w-prose text-sm font-semibold leading-relaxed text-stone-700 sm:text-[0.9375rem]">{data.plainLanguage.lead}</p>
        <p className="mt-8 text-[10px] font-black uppercase tracking-[0.22em] text-orange-600">Predictions may cover</p>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.plainLanguage.predictionTypes.map((p) => (
            <li
              key={p}
              className="rounded-xl border border-orange-100/90 bg-gradient-to-br from-orange-50/40 to-white px-4 py-3 text-sm font-bold text-stone-900 shadow-sm"
            >
              {p}
            </li>
          ))}
        </ul>
        <div className="mt-8">
          <WarningPanel title={data.plainLanguage.importantTitle}>
            <ul className="mt-2 space-y-1.5">
              {data.plainLanguage.importantLines.map((l) => (
                <li key={l}>· {l}</li>
              ))}
            </ul>
          </WarningPanel>
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Odds desk" title={data.oddsAnalysis.headline} />
      <div className="grid gap-5 lg:grid-cols-2">
        <SurfaceCard className="!bg-gradient-to-b from-white to-orange-50/25">
          <p className="text-sm font-semibold text-stone-700">{data.oddsAnalysis.lead}</p>
          <div className="mt-4">
            <CheckList items={data.oddsAnalysis.bullets} color={color} />
          </div>
          <p className="mt-8 text-[10px] font-black uppercase tracking-[0.2em] text-orange-600">Odds help you estimate</p>
          <ul className="mt-3 space-y-2 border-l-2 border-orange-200 pl-4 text-sm font-bold text-stone-800">
            {data.oddsAnalysis.helps.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>
        </SurfaceCard>
        <div className="relative overflow-hidden rounded-2xl border border-orange-400/30 bg-gradient-to-br from-stone-950 via-stone-900 to-orange-950 p-8 text-white shadow-xl">
          <div className="pointer-events-none absolute -right-10 top-0 h-48 w-48 rounded-full bg-orange-500/25 blur-3xl" />
          <BarChart3 className="relative h-10 w-10 text-[#FFD221]" />
          <p className="relative mt-5 text-xl font-black leading-snug">Probability × payout</p>
          <p className="relative mt-3 text-sm font-semibold text-orange-100/75">
            Line shopping on licensed books — tiny edges add up over a season.
          </p>
          <div className="relative mt-8 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-[#FFD221]/90">
            <TrendingUp className="h-4 w-4" />
            Think in edges, not emotions
          </div>
        </div>
      </div>
    </section>

    <section className="space-y-10">
      <BettingSectionHead eyebrow="What people ask" title="COMMON QUESTIONS GHANAIANS ASK" />

      {data.commonQna.map((block) => (
        <div
          key={block.id}
          className="overflow-hidden rounded-[1.5rem] border border-orange-200/50 bg-stone-50/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]"
        >
          <div className="flex flex-wrap items-center gap-4 border-b border-orange-100/90 bg-gradient-to-r from-orange-500/10 via-white to-transparent px-6 py-5 sm:px-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-900 text-lg font-black text-[#FFD221] shadow-lg">
              ?
            </span>
            <h3 className="flex-1 text-lg font-black leading-snug text-stone-950 sm:text-xl">{block.question}</h3>
          </div>

          <div className="space-y-4 p-6 sm:p-8">
            {"truth" in block ? (
              <>
                <div className="rounded-2xl bg-white p-5 ring-1 ring-orange-100/80 sm:p-6">
                  <p className="text-sm font-semibold leading-relaxed text-stone-800">
                    <span className="font-black text-orange-600">Truth: </span>
                    {block.truth}
                  </p>
                </div>
                <div className="rounded-2xl border-2 border-red-300/80 bg-gradient-to-br from-red-50 to-white p-5 sm:p-6">
                  <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-red-900">
                    <ShieldAlert className="h-4 w-4" />
                    {block.warningTitle}
                  </p>
                  <p className="mt-2 text-sm font-semibold text-red-950">{block.warningBody}</p>
                </div>
                <div className="rounded-2xl border border-emerald-200/90 bg-emerald-50/50 p-5 sm:p-6">
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-900">{block.betterTitle}</p>
                  <div className="mt-3">
                    <CheckList items={block.better} color="#059669" />
                  </div>
                </div>
              </>
            ) : null}

            {"factors" in block ? (
              <div className="rounded-2xl bg-white p-6 ring-1 ring-orange-100/80">
                <p className="text-sm font-semibold text-stone-700">{block.intro}</p>
                <div className="mt-4">
                  <CheckList items={block.factors} color={color} />
                </div>
                <p className="mt-8 text-[10px] font-black uppercase tracking-[0.18em] text-orange-600">{block.analysisTitle}</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {block.analysisBlocks.map((cell) => {
                    const Ic = iconFor(cell.icon);
                    return (
                      <div
                        key={cell.title}
                        className="group rounded-2xl border border-stone-200/80 bg-gradient-to-b from-white to-stone-50/80 p-4 transition hover:border-orange-300 hover:shadow-md"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-900 text-white transition group-hover:bg-orange-500">
                          <Ic className="h-5 w-5" />
                        </div>
                        <p className="mt-3 text-sm font-black text-stone-950">{cell.title}</p>
                        {cell.example ? (
                          <p className="mt-1 text-xs font-bold text-orange-700">Example: {cell.example}</p>
                        ) : null}
                        {cell.body ? <p className="mt-1 text-xs font-semibold text-stone-600">{cell.body}</p> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {"example" in block && "rules" in block ? (
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-2xl bg-white p-6 ring-1 ring-orange-100/80">
                  <p className="text-sm font-semibold text-stone-700">{block.lead}</p>
                  <div className="mt-5 rounded-2xl bg-gradient-to-br from-stone-900 to-stone-950 p-6 text-white shadow-inner">
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#FFD221]">Worked example</p>
                    <dl className="mt-4 space-y-3 text-sm">
                      <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                        <dt className="text-white/55">Odds</dt>
                        <dd className="font-black">{block.example.odds}</dd>
                      </div>
                      <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                        <dt className="text-white/55">Stake</dt>
                        <dd className="font-black">{block.example.stake}</dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="text-white/55">Possible return</dt>
                        <dd className="font-black text-[#FFD221]">{block.example.returns}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  {block.rules.map((r) => (
                    <div
                      key={r.label}
                      className="rounded-2xl border-l-4 bg-white px-5 py-4 shadow-sm"
                      style={{ borderLeftColor: color }}
                    >
                      <p className="text-sm font-black text-stone-950">{r.label}</p>
                      <p className="mt-1 text-sm font-semibold text-stone-600">{r.hint}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {"simple" in block ? (
              <div className="rounded-2xl bg-white p-6 ring-1 ring-orange-100/80 sm:p-8">
                <p className="text-sm font-semibold leading-relaxed text-stone-800">{block.simple}</p>
                <p className="mt-5 rounded-xl border border-orange-200/80 bg-orange-50/50 p-4 text-sm font-semibold text-stone-800">
                  {block.example}
                </p>
                <p className="mt-6 text-xs font-black uppercase tracking-[0.14em] text-orange-600">Value betting needs</p>
                <div className="mt-3">
                  <CheckList items={block.needs} color={color} />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </section>

    <section>
      <BettingSectionHead eyebrow="Markets" title={data.markets.headline} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.markets.items.map((m) => (
          <div
            key={m.title}
            className="relative overflow-hidden rounded-2xl border border-orange-100/90 bg-gradient-to-b from-white via-white to-orange-50/30 p-5 shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            <div className="absolute right-3 top-3 h-16 w-16 rounded-full bg-orange-400/10 blur-2xl" />
            <p className="relative text-sm font-black text-stone-950">{m.title}</p>
            {m.bullets ? (
              <ul className="relative mt-3 space-y-1.5 text-sm font-semibold text-stone-700">
                {m.bullets.map((b) => (
                  <li key={b}>· {b}</li>
                ))}
              </ul>
            ) : null}
            {m.body ? <p className="relative mt-3 text-sm font-semibold text-stone-600">{m.body}</p> : null}
          </div>
        ))}
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-red-200/60 bg-gradient-to-br from-red-50/80 to-white p-6 sm:p-8">
        <BettingSectionHead eyebrow="Loss patterns" title={data.whyLose.headline} />
        <ul className="mt-2 space-y-3">
          {data.whyLose.mistakes.map((x) => (
            <li key={x} className="flex gap-3 text-sm font-semibold text-stone-900">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
              {x}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-col justify-center">
        <WarningPanel title="Big problem">{data.whyLose.problem}</WarningPanel>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Reality check" title={data.expertReality.headline} />
      <div className="grid gap-5 lg:grid-cols-3">
        <SurfaceCard className="lg:col-span-2 !py-6">
          <CheckList items={data.expertReality.lines} color={color} />
        </SurfaceCard>
        <div className="rounded-2xl border-2 border-orange-300/50 bg-gradient-to-b from-orange-100/50 to-white p-6 shadow-md">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-orange-900">{data.expertReality.socialTitle}</p>
          <ul className="mt-4 space-y-2 text-sm font-bold text-stone-900">
            {data.expertReality.socialFlags.map((f) => (
              <li key={f} className="flex gap-2 border-b border-orange-100/80 pb-2 last:border-0">
                <span className="font-black text-orange-500">!</span>
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-emerald-200/90 bg-emerald-50/40 p-6">
          <p className="text-xs font-black uppercase text-emerald-900">{data.expertReality.goodTitle}</p>
          <div className="mt-3">
            <CheckList items={data.expertReality.good} color="#059669" />
          </div>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-stone-900 p-6 text-white">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FFD221]">Not quality analysis</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-white/85">
            {data.expertReality.bad.map((b) => (
              <li key={b}>· {b}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Discipline" title={data.bankroll.headline} />
      <p className="mb-6 max-w-prose text-sm font-semibold text-stone-600">{data.bankroll.lead}</p>
      <div className="grid gap-5 md:grid-cols-3">
        {data.bankroll.rules.map((r, i) => (
          <div
            key={r.title}
            className="relative overflow-hidden rounded-2xl border border-orange-200/60 bg-white p-6 pt-12 shadow-md"
          >
            <span className="absolute left-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg bg-stone-900 text-sm font-black text-[#FFD221]">
              {i + 1}
            </span>
            <p className="text-sm font-black text-stone-950">{r.title}</p>
            <p className="mt-2 text-sm font-semibold text-stone-600">{r.body}</p>
          </div>
        ))}
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Compliance" title={data.responsible.headline} />
      <div className="overflow-hidden rounded-[1.75rem] border border-amber-200/70 bg-gradient-to-br from-amber-50/30 via-white to-white">
        <div className="border-b border-amber-100/80 bg-amber-100/25 px-6 py-4 sm:px-8">
          <p className="text-sm font-semibold text-stone-800">{data.responsible.commission}</p>
        </div>
        <div className="grid gap-6 p-6 sm:grid-cols-2 sm:p-8">
          <div className="rounded-2xl border border-amber-200/90 bg-white/90 p-5 shadow-sm">
            <p className="text-xs font-black uppercase text-amber-900">{data.responsible.warningTitle}</p>
            <ul className="mt-3 space-y-2 text-sm font-semibold text-stone-800">
              {data.responsible.warningSigns.map((w) => (
                <li key={w}>· {w}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-200/90 bg-emerald-50/50 p-5">
            <p className="text-xs font-black uppercase text-emerald-900">{data.responsible.tipsTitle}</p>
            <div className="mt-3">
              <CheckList items={data.responsible.tips} color="#059669" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Myth busting" title={data.myths.headline} />
      <div className="grid gap-4 sm:grid-cols-2">
        {data.myths.items.map((m) => (
          <div
            key={m.myth}
            className="grid overflow-hidden rounded-2xl border border-stone-200/90 shadow-md sm:grid-cols-2"
          >
            <div className="border-b border-stone-100 bg-red-50/70 px-5 py-4 sm:border-b-0 sm:border-r">
              <p className="text-[10px] font-black uppercase tracking-wider text-red-800">Myth</p>
              <p className="mt-2 text-sm font-black text-red-950">{m.myth}</p>
            </div>
            <div className="bg-emerald-50/60 px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-wider text-emerald-900">Reality</p>
              <p className="mt-2 text-sm font-semibold text-emerald-950">{m.truth}</p>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Line shopping" title={data.oddsComparison.headline} />
      <div className="rounded-2xl border border-orange-200/60 bg-gradient-to-r from-orange-50/40 via-white to-amber-50/30 p-6 sm:p-8">
        <p className="max-w-prose text-sm font-semibold text-stone-700">{data.oddsComparison.lead}</p>
        <ul className="mt-6 flex flex-wrap gap-2">
          {data.oddsComparison.compare.map((c) => (
            <li
              key={c}
              className="rounded-full border border-orange-200/90 bg-white px-4 py-2 text-xs font-black text-stone-900 shadow-sm"
            >
              {c}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Editorial" title={data.contentIdeas.headline} />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.contentIdeas.groups.map((g) => (
          <div
            key={g.title}
            className="group rounded-2xl border border-stone-900/8 bg-stone-900 p-5 text-white shadow-lg transition hover:border-orange-500/40"
          >
            <div className="mb-3 inline-flex rounded-lg bg-orange-500/20 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-[#FFD221]">
              Lane
            </div>
            <p className="text-base font-black text-white">{g.title}</p>
            <ul className="mt-4 space-y-2 text-sm font-semibold text-white/75">
              {g.bullets.map((b) => (
                <li key={b} className="flex gap-2">
                  <span className="text-orange-400">–</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section className="overflow-hidden rounded-[1.75rem] border border-orange-400/30 bg-gradient-to-br from-[#1a1512] via-stone-900 to-orange-950 p-8 text-white shadow-2xl sm:p-10">
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="h-8 w-8 text-[#FFD221]" />
        <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">
          Content funnel
        </span>
      </div>
      <h3 className="mt-5 text-2xl font-black leading-tight sm:text-3xl">{data.funnel.headline}</h3>
      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div className="space-y-5">
          {[
            { k: "Hook", v: data.funnel.hook },
            { k: "Education", v: data.funnel.education },
            { k: "Trust", v: data.funnel.trust },
          ].map(({ k, v }) => (
            <div key={k} className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-300/95">{k}</p>
              <p className="mt-2 text-sm font-semibold text-white/92">{v}</p>
            </div>
          ))}
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">Conversion paths</p>
          <ul className="mt-4 space-y-3 text-sm font-bold text-white/90">
            {data.funnel.conversion.map((c) => (
              <li key={c} className="flex gap-3 rounded-xl border border-white/10 bg-black/25 px-4 py-3">
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD221]" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="rounded-[1.75rem] border-2 border-orange-300/60 bg-gradient-to-br from-[#FFD221]/25 via-white to-orange-50/40 p-8 shadow-lg sm:p-10">
      <BettingSectionHead eyebrow="Next steps" title={data.strongCta.headline} />
      <ul className="mt-4 space-y-4">
        {data.strongCta.items.map((item) => (
          <li key={item} className="flex items-start gap-4 text-sm font-black text-stone-900 sm:text-[0.9375rem]">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-stone-900 text-sm text-[#FFD221] shadow-md">
              →
            </span>
            {item}
          </li>
        ))}
      </ul>
    </section>

    <section>
      <BettingSectionHead eyebrow="Closing" title={data.takeaway.headline} />
      <div className="rounded-2xl border border-stone-200 bg-gradient-to-b from-white to-stone-50/50 p-6 sm:p-8">
        {data.takeaway.paragraphs.map((p) => (
          <p key={p} className="mb-4 last:mb-0 text-sm font-semibold leading-relaxed text-stone-700 sm:text-[0.9375rem]">
            {p}
          </p>
        ))}
        <div className="mt-6 rounded-xl border-l-4 border-orange-500 bg-orange-50/40 p-4 text-sm font-semibold text-stone-800">
          {data.takeaway.footnote}
        </div>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Quick answers" title="FAQs" />
      <div className="rounded-2xl border border-orange-100/90 bg-white p-4 shadow-md ring-1 ring-orange-950/[0.05] sm:p-6">
        <div className="space-y-2 divide-y divide-orange-100/80">
          {data.faqs.map((faq) => (
            <div key={faq.q} className="pt-2 first:pt-0">
              <FAQItem q={faq.q} a={faq.a} color={color} />
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export const SportsBettingTipsArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-12">
    <section>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <BettingSectionHead eyebrow="Library" title="Articles & guides" subtitle={data.funnelTagline} />
        <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-black text-orange-900">
          {data.articles.length} pieces
        </span>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section className="overflow-hidden rounded-[1.5rem] border border-orange-200/70 bg-gradient-to-br from-stone-900 via-stone-900 to-orange-950 p-8 text-white sm:p-10">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em] text-[#FFD221]">{data.editorialPlaybook.leadCtasHeading}</p>
          <h3 className="text-xl font-black sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-white/80">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD221]" />
                {c}
              </li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-[#FFD221] px-8 py-4 text-sm font-black text-black shadow-lg transition hover:brightness-105"
        >
          Register free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const SportsBettingTipsMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <BettingSectionHead
          eyebrow="Watch & listen"
          title="Videos & podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
        />
        <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-black text-orange-900">{total} featured</span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="sbt-media-videos-heading" className="min-w-0 rounded-2xl border border-orange-100/90 bg-white/90 p-4 shadow-md ring-1 ring-orange-950/[0.04] sm:p-6">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Sports betting education videos"
            items={videos}
            header={
              <h3 id="sbt-media-videos-heading" className="text-lg font-black text-stone-950 sm:text-xl">
                Videos
              </h3>
            }
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="sbt-media-podcasts-heading" className="min-w-0 rounded-2xl border border-orange-100/90 bg-white/90 p-4 shadow-md ring-1 ring-orange-950/[0.04] sm:p-6">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Sports betting podcasts"
            items={podcasts}
            header={
              <h3 id="sbt-media-podcasts-heading" className="text-lg font-black text-stone-950 sm:text-xl">
                Podcasts
              </h3>
            }
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
        </section>
      ) : null}
    </div>
  );
};
